<template>
  <v-container class="hyp-map-controls pa-3 ma-0">
    <!-- Points Control -->
    <div class="mb-4">
      <h3 class="pull-left">Points</h3>
      <!-- Last refinement, decided to not have feature for points toggle, keeping this for now -->
      <!-- <v-switch
        class="pull-right mt-0"
        v-model="mapPointsControlEnabled"
        @change="
          toggleMapControl('mapPointsControlEnabled', mapPointsControlEnabled)
        "
        hide-details
      ></v-switch> -->
      <div class="clearfix"></div>
    </div>

    <v-expansion-panels readonly accordion flat elevation-0>
      <v-expansion-panel v-for="section in pointsSections" :key="section.id">
        <v-expansion-panel-header hide-actions class="pa-2">
          <template>
            <div>
              <h4 class="hyp-map-controls-section-title pl-2 mt-1">
                {{ section.title }}
                <a
                  v-if="section.filterLinkPanel"
                  class="ml-5 caption"
                  @click="handleClickFilter(section)"
                  >Filter</a
                >
              </h4>
              <v-checkbox
                class="ma-0 pa-0 pull-right"
                v-model="section.visible"
                hide-details
                @change="handleChangePoints"
              ></v-checkbox>
            </div>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <component
            v-show="section.contentComp"
            v-bind:is="section.contentComp"
          ></component>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- External Layers Control -->
    <div v-if="isMapControlsExternalFeatureUnlocked">
      <div class="mt-10 mb-4">
        <h3 class="pull-left">External Layers</h3>
        <!--
        <v-switch
          class="pull-right mt-0"
          v-model="mapExternalControlEnabled"
          @change="
            toggleMapControl(
              'mapExternalControlEnabled',
              mapExternalControlEnabled
            )
          "
          hide-details
        ></v-switch>
        -->
        <div class="clearfix"></div>
      </div>

      <v-expansion-panels
        v-show="mapExternalControlSelected"
        accordion
        flat
        elevation-0
        class="mb-3"
      >
        <v-expansion-panel>
          <v-expansion-panel-header hide-actions class="pa-2">
            <template v-slot:default="{ open }">
              <div>
                <v-icon>
                  <template v-if="open">mdi-chevron-up</template>
                  <template v-else>mdi-chevron-down</template>
                </v-icon>
                <h4
                  v-if="mapExternalControlSelected"
                  class="hyp-map-controls-section-title pl-2 mt-1"
                >
                  <v-icon
                    x-small
                    color="gray"
                    class="mr-2"
                    title="Remove layer"
                    @click.stop="handleClickRemoveExternal"
                    >fas fa-times</v-icon
                  >
                  <span>{{ mapExternalControlSelected.title }}</span>
                </h4>
                <v-checkbox
                  class="ma-0 pa-0 pull-right"
                  v-model="mapExternalControlEnabled"
                  hide-details
                  @change="handleChangeExternal"
                  @click.stop
                ></v-checkbox>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content eager>
            <MapControlsExternal></MapControlsExternal>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-btn color="primary" x-small @click="handleClickAddExternal">
        Select Map Layer
      </v-btn>
    </div>

    <!-- Overlays Control -->
    <div class="mt-10 mb-4">
      <h3 class="pull-left">Overlays</h3>
      <!-- AAAS-8637 - Decided to not have feature for overlays toggle, keeping this for now -->
      <!-- <v-switch
        class="pull-right mt-0"
        v-model="mapOverlaysControlEnabled"
        @change="
          toggleMapControl(
            'mapOverlaysControlEnabled',
            mapOverlaysControlEnabled
          )
        "
        hide-details
      ></v-switch> -->
      <div class="clearfix"></div>
    </div>

    <v-radio-group
      hide-details
      class="pa-0 ma-0"
      v-model="mapOverlaysControlSelectedType"
      @click.stop
    >
      <v-expansion-panels accordion flat elevation-0>
        <v-expansion-panel
          v-for="section in overlaysSections"
          :key="section.id"
        >
          <v-expansion-panel-header hide-actions class="pa-2">
            <template v-slot:default="{ open }">
              <div>
                <v-icon>
                  <template v-if="open">mdi-chevron-up</template>
                  <template v-else>mdi-chevron-down</template>
                </v-icon>
                <h4 class="hyp-map-controls-section-title pl-2">
                  {{ section.title }}
                </h4>
                <v-radio
                  class="ma-0 pa-0 pull-right"
                  hide-details
                  :value="section"
                  @click.stop="handleChangeOverlays"
                >
                </v-radio>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content eager>
            <component
              v-if="section.contentComp"
              v-bind:is="section.contentComp"
            ></component>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-radio-group>

    <!-- Base Layer Control -->
    <div class="mt-10 mb-4">
      <h3>Base Layer</h3>
    </div>
    <BaseLayer :map-id="mapId" :options="baseLayerControlOptions"></BaseLayer>
  </v-container>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP_CONTROLS,
  MODULE_PREFIX_HOME,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_MAP_CONTROLS_PARAMS,
  GET_SELECTOR_PANEL,
  GET_MAP_CONTROLS_EXTERNAL_REFRESH_MAP,
  GET_MAP_CONTROLS_EXTERNAL_SOURCE_ID,
} from "@/store/actions.type";

import {
  POINTS_SECTIONS_ENABLED_DEFAULT,
  OVERLAYS_SECTIONS_ENABLED_DEFAULT,
  OVERLAYS_SECTIONS_SELECTED_TYPE_DEFAULT,
  EXTERNAL_SECTIONS_ENABLED_DEFAULT,
  EXTERNAL_SECTIONS_SELECTED_ID_DEFAULT,
  POINTS_SECTIONS,
  OVERLAYS_SECTIONS,
} from "@/common/constants/mapControls";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getExternalLayersConfig } from "@/services/mapControls/mapControls.service";

const EMITTED_ACTIONS = {
  OPEN_LIBRARY: "open-library",
};

export default {
  name: "MapControls",
  components: {
    BaseLayer: () => import("@/components/map/BaseLayer"),
    MapControlsOverlayRiskRatings: () =>
      import("@/components/mapControls/MapControlsOverlayRiskRatings"),
    MapControlsOverlayPulse: () =>
      import("@/components/mapControls/MapControlsOverlayPulse"),
    MapControlsOverlayPowerOutage: () =>
      import("@/components/mapControls/MapControlsOverlayPowerOutage"),
    MapControlsPointsHotspots: () =>
      import("@/components/mapControls/MapControlsPointsHotspots"),
    MapControlsPointsMyIncidents: () =>
      import("@/components/mapControls/MapControlsPointsMyIncidents"),
    MapControlsPointsAssets: () =>
      import("@/components/mapControls/MapControlsPointsAssets"),
    MapControlsExternal: () =>
      import("@/components/mapControls/MapControlsExternal"),
  },
  data: () => ({
    mapPointsControlEnabledDefault: POINTS_SECTIONS_ENABLED_DEFAULT,
    mapOverlaysControlEnabledDefault: OVERLAYS_SECTIONS_ENABLED_DEFAULT,
    mapOverlaysControlSelectedTypeDefault: OVERLAYS_SECTIONS_SELECTED_TYPE_DEFAULT,
    mapExternalControlEnabledDefault: EXTERNAL_SECTIONS_ENABLED_DEFAULT,
    mapExternalControlSelectedIdDefault: EXTERNAL_SECTIONS_SELECTED_ID_DEFAULT,
    overlaysSections: OVERLAYS_SECTIONS,
    pointsSections: POINTS_SECTIONS,
    mapPointsControlEnabled: false,
    mapOverlaysControlEnabled: false,
    mapOverlaysControlSelectedType: null,
    mapExternalControlEnabled: false,
    mapExternalControlSelected: null,
    externalConfig: undefined,
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
      mapControlParams: MODULE_PREFIX_MAP_CONTROLS + "params",
      externalSourceId: MODULE_PREFIX_MAP_CONTROLS + "externalSourceId",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    isMapControlsExternalFeatureUnlocked() {
      return this.featureUnlocked("map-controls-external-layers");
    },
    mapId() {
      return MAIN_MAP_ID;
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    baseLayerControlOptions() {
      return {
        showInline: true,
        position: "relative",
        btnSize: 36,
        bottom: null,
        right: null,
      };
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      this.externalConfig = await getExternalLayersConfig();

      // get pref/defaults
      this.getPrefs();
      // Set map controls from pref/defaults
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapPointsControlEnabled = this.mapPointsControlEnabled;
      mapControlParams.mapOverlaysControlEnabled = this.mapOverlaysControlEnabled;
      mapControlParams.mapExternalControlEnabled = this.mapExternalControlEnabled;
      mapControlParams.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType?.id;
      mapControlParams.mapExternalControlSelectedId = this.mapExternalControlSelected?.id;
      mapControlParams.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });

      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_EXTERNAL_REFRESH_MAP,
        mapControlParams
      );
      this.updatePref();
    },
    getPrefs() {
      this.mapPointsControlEnabled =
        this.preferences?.mapControls?.mapPointsControlEnabled ??
        this.mapPointsControlEnabledDefault;
      this.mapOverlaysControlEnabled = true; // To make sure if any user already got overlays disabled in pref, they do see overlay after toogle feature is hidden (AAAS-8637)
      // this.preferences?.mapControls?.mapOverlaysControlEnabled ??
      // this.mapOverlaysControlEnabledDefault;
      this.mapExternalControlEnabled =
        this.preferences?.mapControls?.mapExternalControlEnabled ??
        this.mapExternalControlEnabledDefault;

      const mapOverlaysControlSelectedTypeId =
        this.preferences?.mapControls?.mapOverlaysControlSelectedType ??
        this.mapOverlaysControlSelectedTypeDefault;
      this.mapOverlaysControlSelectedType = this.overlaysSections.find(
        (s) => s.id === mapOverlaysControlSelectedTypeId
      );

      this.pointsSections = this.preferences?.mapControls
        ?.mapPointsControlSelectedTypes
        ? this.pointsSections.map((section) => {
            section.visible =
              this.preferences?.mapControls?.mapPointsControlSelectedTypes.includes(
                section.id
              ) ?? section.visibleDefault;
            return section;
          })
        : this.pointsSections.map((section) => {
            section.visible = section.visibleDefault;
            return section;
          });

      const mapExternalControlSelectedId =
        this.preferences?.mapControls?.mapExternalControlSelectedId ??
        this.mapExternalControlSelectedIdDefault;

      this.mapExternalControlSelected = lodashFind(this.externalConfig, {
        id: mapExternalControlSelectedId,
      });
    },
    updatePref() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.mapControls = preferences.mapControls || {};
      preferences.mapControls.mapPointsControlEnabled = this.mapPointsControlEnabled;
      preferences.mapControls.mapOverlaysControlEnabled = this.mapOverlaysControlEnabled;
      preferences.mapControls.mapExternalControlEnabled = this.mapExternalControlEnabled;
      preferences.mapControls.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType?.id;
      preferences.mapControls.mapExternalControlSelectedId = this.mapExternalControlSelected?.id;
      preferences.mapControls.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    toggleMapControl(type, visible) {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams[type] = visible;
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    handleChangePoints() {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    handleChangeOverlays() {
      // let mapControlParams = lodashCloneDeep(this.mapControlParams);
      // mapControlParams.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType.id;
      // this.$store.dispatch(
      //   MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
      //   mapControlParams
      // );
      // this.updatePref();

      //AAAS-8637 - Unselect on click when already selected
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      if (
        mapControlParams.mapOverlaysControlSelectedType !==
        this.mapOverlaysControlSelectedType?.id
      ) {
        mapControlParams.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType.id;
      } else {
        this.mapOverlaysControlSelectedType = null;
        mapControlParams.mapOverlaysControlSelectedType = null;
      }
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    handleChangeExternal() {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapExternalControlEnabled = this.mapExternalControlEnabled;
      mapControlParams.mapExternalControlSelectedId = this.mapExternalControlSelected?.id;
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_EXTERNAL_REFRESH_MAP,
        mapControlParams
      );
      this.updatePref();
    },
    handleClickAddExternal() {
      this.$emit(EMITTED_ACTIONS.OPEN_LIBRARY);
    },
    handleClickRemoveExternal() {
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_EXTERNAL_SOURCE_ID,
        null
      );
      this.mapExternalControlSelected = null;
      this.handleChangeExternal();
    },
    handleClickFilter(section) {
      const selectorPanel = lodashCloneDeep(this.selectorPanel);
      selectorPanel.selected = section.filterLinkPanel;
      selectorPanel.visible = true;
      selectorPanel.isSelected = true;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        selectorPanel
      );
    },
  },
  watch: {
    externalSourceId: {
      handler() {
        const externalSource = lodashFind(this.externalConfig, {
          id: this.externalSourceId,
        });
        this.mapExternalControlSelected = externalSource;
        this.mapExternalControlEnabled = true;
        this.handleChangeExternal();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.hyp-map-controls {
  width: 350px;
  max-height: 100vh !important;
  overflow-y: auto;
}
.hyp-map-controls .v-expansion-panel {
  border-bottom: 1px solid #dddddd;
}
.hyp-map-controls .v-expansion-panels {
  border-top: 1px solid #dddddd;
}
.hyp-map-controls-section-title {
  display: inline-block;
}
</style>
